/**
 * Read-side hooks for Hyperliquid user state.
 *
 * These are the canonical query points — any component needing server data
 * goes through these (directly, or via a feature-scoped wrapper in
 * `features/trade/hooks/`). Do not call `InfoClient` methods from components.
 *
 * Queries auto-disable when no wallet is connected (`enabled: address !== null`).
 * WS pushes from `./use-hyper-user-events.ts` mutate these caches in place via
 * `setQueryData`, so the `refetchInterval` is just a backstop, not the primary
 * freshness mechanism.
 *
 * `dexAbstraction` (HIP-3) accounts keep their actual collateral / positions
 * on a non-main DEX (currently xyz). `useHyperAccount` queries both main and
 * xyz `clearinghouseState` and merges them via `mergeAcrossDexes` — summary
 * scalars are summed, positions are concatenated and tagged. The xyz raw
 * response is also cached at `queryKeys.hyper.accountXyz(address)` so the
 * main-DEX WS sync (`useHyperMainSync`) can re-merge instead of dropping xyz
 * contributions on every push.
 *
 * Write-side companion: `./use-trading-actions.ts`.
 * Account model (margin, unified vs non-unified, balance rules): see `./ACCOUNT.md`.
 */
import type { InfoClient } from "@nktkas/hyperliquid";
import * as Sentry from "@sentry/react";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
	getInfoClient,
	stripDexPrefix,
	XYZ_DEX_NAME,
} from "#/features/trade/lib/hyperliquid";
import { toNumber } from "#/lib/formatters";
import { REFERRAL_CODE } from "./builder-fee";
import { queryKeys } from "./query-keys";
import { useExchangeClient } from "./use-exchange-client";
import { useWallet } from "./use-wallet";

const sumStr = (
	a: string | number | null | undefined,
	b: string | number | null | undefined,
) => String(toNumber(a) + toNumber(b));

/**
 * Merge a per-DEX clearinghouseState into a base AccountState.
 *
 * `dexAbstraction` users (HIP-3) keep their actual collateral/positions on a
 * non-main DEX (currently xyz). Hyperliquid returns a separate
 * clearinghouseState per DEX; if we only return main, summary fields
 * (accountValue, totalMarginUsed, totalNtlPos, withdrawable, ...) drop the
 * other-DEX contribution and the UI shows ~$0 for active traders.
 *
 * This sums the summary scalars across DEXes so balance / margin / equity
 * displays reflect the user's true cross-DEX state. Positions are tagged
 * with `__dex: "xyz"` so per-DEX filters elsewhere keep working. The main
 * DEX's own `withdrawable` is preserved as `__mainWithdrawable` — actions
 * scoped to the main DEX (`withdraw3`) must not use the merged sum; read it
 * via `mainWithdrawable()`.
 */
export function mergeAcrossDexes(
	main: AccountState,
	xyz: AccountState,
): AccountState {
	const xyzPositions: AssetPosition[] = xyz.assetPositions.map((item) => ({
		...item,
		__dex: "xyz",
		position: {
			...item.position,
			coin: stripDexPrefix(item.position.coin),
		},
	}));
	return {
		...main,
		marginSummary: {
			...main.marginSummary,
			accountValue: sumStr(
				main.marginSummary.accountValue,
				xyz.marginSummary.accountValue,
			),
			totalMarginUsed: sumStr(
				main.marginSummary.totalMarginUsed,
				xyz.marginSummary.totalMarginUsed,
			),
			totalNtlPos: sumStr(
				main.marginSummary.totalNtlPos,
				xyz.marginSummary.totalNtlPos,
			),
			totalRawUsd: sumStr(
				main.marginSummary.totalRawUsd,
				xyz.marginSummary.totalRawUsd,
			),
		},
		crossMarginSummary: {
			...main.crossMarginSummary,
			accountValue: sumStr(
				main.crossMarginSummary.accountValue,
				xyz.crossMarginSummary.accountValue,
			),
			totalMarginUsed: sumStr(
				main.crossMarginSummary.totalMarginUsed,
				xyz.crossMarginSummary.totalMarginUsed,
			),
			totalNtlPos: sumStr(
				main.crossMarginSummary.totalNtlPos,
				xyz.crossMarginSummary.totalNtlPos,
			),
			totalRawUsd: sumStr(
				main.crossMarginSummary.totalRawUsd,
				xyz.crossMarginSummary.totalRawUsd,
			),
		},
		crossMaintenanceMarginUsed: sumStr(
			main.crossMaintenanceMarginUsed,
			xyz.crossMaintenanceMarginUsed,
		),
		withdrawable: sumStr(main.withdrawable, xyz.withdrawable),
		__mainWithdrawable: String(toNumber(main.withdrawable)),
		assetPositions: [...main.assetPositions, ...xyzPositions],
	};
}

/**
 * Main-DEX-only withdrawable — the amount `withdraw3` can actually pull.
 * Merged states carry it as `__mainWithdrawable`; an un-merged state's
 * `withdrawable` is already main-only, so the fallback stays correct even
 * for cache writers that bypass `mergeAcrossDexes` (those don't sum either).
 */
export function mainWithdrawable(
	accountState: AccountState | undefined,
): number {
	return toNumber(
		accountState?.__mainWithdrawable ?? accountState?.withdrawable,
	);
}

type AccountState = Awaited<ReturnType<InfoClient["clearinghouseState"]>> & {
	/** Main-DEX-only withdrawable, set by mergeAcrossDexes (withdraw3 limit). */
	__mainWithdrawable?: string;
};
type SpotState = Awaited<ReturnType<InfoClient["spotClearinghouseState"]>>;
type OpenOrder = Awaited<
	ReturnType<InfoClient["frontendOpenOrders"]>
>[number] & {
	__dex?: "xyz";
};
type AssetPosition = AccountState["assetPositions"][number] & {
	__dex?: "xyz";
};
type UserFill = Awaited<ReturnType<InfoClient["userFills"]>>[number];
type LedgerUpdate = Awaited<
	ReturnType<InfoClient["userNonFundingLedgerUpdates"]>
>[number];
type UserFundingUpdate = Awaited<ReturnType<InfoClient["userFunding"]>>[number];
type HistoricalOrder = Awaited<
	ReturnType<InfoClient["historicalOrders"]>
>[number];
type TwapHistoryItem = Awaited<ReturnType<InfoClient["twapHistory"]>>[number];
type TwapSliceFill = Awaited<
	ReturnType<InfoClient["userTwapSliceFills"]>
>[number];

export type {
	AccountState,
	AssetPosition,
	SpotState,
	OpenOrder,
	UserFill,
	LedgerUpdate,
	UserFundingUpdate,
	HistoricalOrder,
	TwapHistoryItem,
	TwapSliceFill,
};

// ── xyz cache diagnostics ────────────────────────────────────────────
//
// Sentry events fire at each xyz failure point so you can correlate timing,
// frequency, and cause of HIP-3 balance loss.

let lastXyzDiagnosticAt = 0;

export function captureXyzDiagnostic(
	event: string,
	level: "warning" | "error",
	extra?: Record<string, unknown>,
) {
	const now = Date.now();
	if (now - lastXyzDiagnosticAt < 30_000) return;
	lastXyzDiagnosticAt = now;
	const msg = `[xyz-cache] ${event}`;
	const log = level === "error" ? console.error : console.warn;
	log(msg, extra ?? "");
	Sentry.withScope((scope) => {
		scope.setTag("xyz_diagnostic", event);
		scope.setLevel(level);
		if (extra) scope.setExtras(extra);
		Sentry.captureMessage(msg);
	});
}

// Module-level fallback for xyz account state.  The TanStack Query cache
// entry (`accountXyz`) has no active observer, so TanStack garbage-collects
// it after gcTime (5 min default).  When the GC fires before the next REST
// refetch, the main-DEX WS sync loses the xyz contribution and HIP-3
// balances flash to $0.  This Map survives GC because it is a plain JS
// reference, keyed by wallet address.
const xyzAccountFallback = new Map<string, AccountState>();

export function getXyzAccountFallback(
	address: string,
): AccountState | undefined {
	return xyzAccountFallback.get(address);
}

export function setXyzAccountFallback(address: string, data: AccountState) {
	xyzAccountFallback.set(address, data);
}

let referralSettling = false;

export function useHyperAccount() {
	const { address } = useWallet();
	const infoClient = getInfoClient();
	const client = useExchangeClient();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.account(address),
		queryFn: async () => {
			const [main, xyzResult] = await Promise.all([
				infoClient.clearinghouseState({
					user: address as `0x${string}`,
				}),
				infoClient
					.clearinghouseState({
						user: address as `0x${string}`,
						dex: XYZ_DEX_NAME,
					})
					.catch(() => undefined),
			]);
			if (xyzResult !== undefined) {
				queryClient.setQueryData(
					queryKeys.hyper.accountXyz(address),
					xyzResult,
				);
				xyzAccountFallback.set(address!, xyzResult);
			}
			const cachedXyz =
				xyzResult ??
				queryClient.getQueryData<AccountState | undefined>(
					queryKeys.hyper.accountXyz(address),
				) ??
				xyzAccountFallback.get(address!);
			if (!cachedXyz) {
				captureXyzDiagnostic("rest-backstop: no xyz data", "error", {
					address,
					source: "queryFn",
				});
				return main;
			}
			return mergeAcrossDexes(main, cachedXyz);
		},
		staleTime: 600_000,
		refetchInterval: 600_000,
		enabled: address !== null,
	});

	const accountState = query.data as AccountState | undefined;
	const positions = useMemo(
		() => accountState?.assetPositions.map((item) => item.position) ?? [],
		[accountState],
	);

	// Exchange actions hard-fail with "Must deposit before performing actions"
	// until the account exists on Hyperliquid, so gate the referral side effect
	// on funding — a deposit flips this value and re-fires the effect exactly
	// when it can succeed. (Builder fee needs no side effect here: every order
	// awaits `ensureBuilderFeeApproved` in the order path.)
	const accountFunded = toNumber(accountState?.marginSummary?.accountValue) > 0;

	useEffect(() => {
		if (!address || !accountFunded) return;
		if (referralSettling) return;
		referralSettling = true;

		getInfoClient()
			.referral({ user: address as `0x${string}` })
			.then((data) => {
				if (data.referredBy !== null) return;
				return client
					.get()
					.then(({ exchangeClient }) =>
						exchangeClient.setReferrer({ code: REFERRAL_CODE }),
					);
			})
			.catch((err) => {
				referralSettling = false;
				console.warn("[referral] Auto-set referrer failed:", err);
			});
	}, [address, accountFunded, client]);

	return {
		...query,
		accountState,
		positions,
	};
}

export function useUserAbstraction() {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.userAbstraction(address),
		queryFn: () =>
			infoClient.userAbstraction({
				user: address as `0x${string}`,
			}),
		staleTime: 60_000,
		enabled: address !== null,
	});

	return {
		...query,
		isUnified: query.data === "unifiedAccount",
		isDexAbstraction: query.data === "dexAbstraction",
	};
}

const EMPTY_OPEN_ORDERS: OpenOrder[] = [];

export function useHyperOpenOrders() {
	const { address } = useWallet();
	const infoClient = getInfoClient();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.openOrders(address),
		queryFn: async () => {
			const [main, xyz] = await Promise.all([
				infoClient.frontendOpenOrders({
					user: address as `0x${string}`,
				}),
				infoClient
					.frontendOpenOrders({
						user: address as `0x${string}`,
						dex: XYZ_DEX_NAME,
					})
					.catch(() => [] as OpenOrder[]),
			]);
			const xyzStripped: OpenOrder[] = (xyz as OpenOrder[]).map((order) => ({
				...order,
				__dex: "xyz",
				coin: stripDexPrefix(order.coin),
			}));
			if (xyzStripped.length === 0) {
				const prev = queryClient.getQueryData<OpenOrder[]>(
					queryKeys.hyper.openOrders(address),
				);
				const oldXyz = (prev ?? []).filter((o) => o.__dex === "xyz");
				if (oldXyz.length > 0) return [...(main as OpenOrder[]), ...oldXyz];
			}
			return [...(main as OpenOrder[]), ...xyzStripped];
		},
		staleTime: 600_000,
		refetchInterval: 600_000,
		enabled: address !== null,
	});

	const openOrders =
		(query.data as OpenOrder[] | undefined) ?? EMPTY_OPEN_ORDERS;

	return {
		...query,
		openOrders,
	};
}

export function useHyperSpotBalances() {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.spotBalances(address),
		queryFn: () =>
			infoClient.spotClearinghouseState({
				user: address as `0x${string}`,
			}),
		staleTime: 300_000,
		refetchInterval: 300_000,
		enabled: address !== null,
	});

	const balances = (query.data as SpotState | undefined)?.balances ?? [];
	return {
		...query,
		balances,
	};
}

export function useHyperLedger(enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.ledger(address),
		queryFn: () =>
			infoClient.userNonFundingLedgerUpdates({
				user: address as `0x${string}`,
				startTime: 0,
			}),
		staleTime: 30_000,
		enabled: address !== null && enabled,
	});

	const ledger = (query.data ?? [])
		.filter(
			(item) => item.delta.type === "deposit" || item.delta.type === "withdraw",
		)
		.sort((a, b) => b.time - a.time);

	return { ...query, ledger };
}

const FILLS_PAGE_SIZE = 100;

export function useHyperFills(_enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useInfiniteQuery({
		queryKey: queryKeys.hyper.fills(address),
		queryFn: ({ pageParam }) =>
			infoClient.userFillsByTime({
				user: address as `0x${string}`,
				startTime: 0,
				// null = "latest" (endTime omitted → server uses now). TanStack v5
				// refetches page 1 with the STORED first pageParam, so pinning
				// Date.now() here would freeze the window at first-fetch time and
				// make every fill after that unreachable by REST forever.
				endTime: pageParam ?? undefined,
				aggregateByTime: true,
				reversed: true,
			}),
		// `as` keeps TPageParam = number | null (plain null would infer `null`
		// and reject getNextPageParam's number).
		initialPageParam: null as number | null,
		getNextPageParam: (lastPage) => {
			if (lastPage.length < FILLS_PAGE_SIZE) return undefined;
			const oldest = lastPage[lastPage.length - 1];
			return oldest ? oldest.time - 1 : undefined;
		},
		staleTime: 300_000,
		refetchInterval: false,
		enabled: address !== null,
	});

	const fills =
		(query.data?.pages.flat() as UserFill[] | undefined)?.map((fill) => ({
			...fill,
			coin: stripDexPrefix(fill.coin),
		})) ?? [];

	return {
		...query,
		fills,
	};
}

const FUNDING_PAGE_SIZE = 500;

export function useHyperFundingHistory(enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useInfiniteQuery({
		queryKey: queryKeys.hyper.fundingHistory(address),
		queryFn: ({ pageParam }) =>
			infoClient.userFunding({
				user: address as `0x${string}`,
				startTime: 0,
				endTime: pageParam,
			}),
		initialPageParam: Date.now(),
		getNextPageParam: (lastPage) => {
			if (lastPage.length < FUNDING_PAGE_SIZE) return undefined;
			let oldestTime = Number.POSITIVE_INFINITY;
			for (const item of lastPage) {
				if (item.time < oldestTime) oldestTime = item.time;
			}
			if (!Number.isFinite(oldestTime)) return undefined;
			return oldestTime - 1;
		},
		staleTime: 600_000,
		refetchInterval: false,
		enabled: address !== null && enabled,
	});

	const fundingHistory = (
		(query.data?.pages.flat() ?? []) as UserFundingUpdate[]
	)
		.map((item) => ({
			...item,
			delta: {
				...item.delta,
				coin: stripDexPrefix(item.delta.coin),
			},
		}))
		.sort((a, b) => b.time - a.time);

	return { ...query, fundingHistory };
}

export function useHyperOrderHistory(enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.orderHistory(address),
		queryFn: () =>
			infoClient.historicalOrders({
				user: address as `0x${string}`,
			}),
		staleTime: 300_000,
		refetchInterval: false,
		enabled: address !== null && enabled,
	});

	const orderHistory = ((query.data ?? []) as HistoricalOrder[]).map(
		(item) => ({
			...item,
			order: {
				...item.order,
				coin: stripDexPrefix(item.order.coin),
			},
		}),
	);

	return { ...query, orderHistory };
}

export function useHyperTwapHistory(_enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.twapHistory(address),
		queryFn: () =>
			infoClient.twapHistory({
				user: address as `0x${string}`,
			}),
		staleTime: 300_000,
		refetchInterval: false,
		enabled: address !== null,
	});

	const twapHistory = ((query.data ?? []) as TwapHistoryItem[]).map((item) => ({
		...item,
		state: {
			...item.state,
			coin: stripDexPrefix(item.state.coin),
		},
	}));

	return { ...query, twapHistory };
}

export function useHyperTwapSliceFills(_enabled = true) {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const query = useQuery({
		queryKey: queryKeys.hyper.twapSliceFills(address),
		queryFn: () =>
			infoClient.userTwapSliceFills({
				user: address as `0x${string}`,
			}),
		staleTime: 300_000,
		refetchInterval: false,
		enabled: address !== null,
	});

	const raw = (query.data ?? []) as TwapSliceFill[];
	const twapSliceFills = raw.map((item) => ({
		...item,
		fill: {
			...item.fill,
			coin: stripDexPrefix(item.fill.coin),
		},
	}));

	return { ...query, twapSliceFills };
}
